import { motion } from "framer-motion";
import React, { useState } from "react";
import { usePageLoading } from "@/hooks/usePageLoading";
import ButtonElement from "@/components/elements/Button/Button";

const Contact = () => {
  const { isLoading } = usePageLoading();
  // State to track errors for each field
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
    message?: string;
  }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    const nameInput = (document.getElementById("name-input") as HTMLInputElement).value.trim();
    const emailInput = (document.getElementById("email-input") as HTMLInputElement).value.trim();
    const phoneInput = (document.getElementById("phone-input") as HTMLInputElement).value.trim();
    const messageInput = (document.getElementById("message-input") as HTMLTextAreaElement).value.trim();

    const newErrors: typeof errors = {};

    if (!nameInput) newErrors.name = "Please fill in this field";
    if (!emailInput) newErrors.email = "Please fill in this field";
    else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(emailInput)) newErrors.email = "Please enter a valid email address";
    }
    if (!phoneInput) {
      newErrors.phone = "Please fill in this field";
    } else {
      const englishNumberPattern = /^[0-9]{10}$/;
      if (!englishNumberPattern.test(phoneInput)) newErrors.phone = "Please enter a valid 10-digit phone number";
    }
    if (!messageInput) newErrors.message = "Please fill in this field";

    // If there are errors, show them and stop submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch("https://formspree.io/f/xoqgejwg", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: nameInput,
          email: emailInput,
          phone: phoneInput,
          message: messageInput,
        }),
      });

      const data = await response.json();
      console.log("Formspree response:", data);

      if (response.ok) {
        alert("Thank you for your message! We will get back to you soon.");
        (document.getElementById("contact-form") as HTMLFormElement).reset();
        setErrors({});
      } else {
        alert("Oops! Something went wrong. Please try again.");
      }
    } catch (error) {
      alert("Error submitting the form. Please try again later.");
      console.error(error);
    }
  };

  return (
    <section
      id="contact-section"
      className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 my-[5rem]"
    >
      {/* Title Section */}
      <div
        style={{
          paddingLeft: "clamp(.2rem, 6vw, 8rem)",
          paddingRight: "clamp(.2rem, 6vw, 8rem)",
          transition: "padding 0.5s ease-in-out",
          marginTop: "clamp(0.2rem, 6vw, 6rem)",
        }}
      >
        <motion.div
          className="custom__divider mt-[1rem]"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={!isLoading ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-container ">
            <p className="text-custom-secolor font-extrabold text-left title-text-size">
              Contact Us
            </p>
          </div>
        </motion.div>
      </div>

      {/* Main Contact Section */}
      <div className="bg-background">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch gap-6 mb-2 px-[clamp(0.2rem,5vw,10rem)] transition-[padding] duration-500 ease-in-out">
          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
            className="w-full lg:w-1/2 flex-grow"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3533.071834163048!2d85.2975386!3d27.6841746!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb196a27a33541%3A0x6f2ba68dd5c4ed69!2sYuki%20Enterprises%20Pvt%20Ltd!5e0!3m2!1sen!2snp!4v1705600583898!5m2!1sen!2snp"
              width="100%"
              height="100%"
              style={{ border: "0" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </motion.div>

          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
            className="w-full lg:w-1/2 flex justify-start items-start flex-grow"
          >
            <div className="w-full px-2 sm:px-4 max-w-[clamp(300px, 100%, 600px)]">
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
                className="text-custom-secolor lg:text-center pb-4 animate-pulse title-text-size font-extrabold text-left"
              >
                Enquire Now
              </motion.p>

              <motion.form
                id="contact-form"
                noValidate
                onSubmit={handleSubmit}
                // No action or method needed here with JS fetch
                className="flex flex-col gap-4 w-full"
              >
                <div>
                  <input
                    id="name-input"
                    type="text"
                    placeholder="Enter Name"
                    className={`bg-white input input-bordered w-full content-text-size ${
                      errors.name ? "border-red-500" : ""
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <input
                    id="email-input"
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    className={`bg-white input input-bordered w-full content-text-size ${
                      errors.email ? "border-red-500" : ""
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <input
                    id="phone-input"
                    type="tel"
                    name="phone"
                    pattern="[0-9]{10}"
                    placeholder="Enter Phone Number"
                    className={`bg-white input input-bordered w-full content-text-size ${
                      errors.phone ? "border-red-500" : ""
                    }`}
                  />
                  {errors.phone && (
                    <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <textarea
                    id="message-input"
                    name="message"
                    placeholder="Enter Message"
                    className={`bg-white input input-bordered input-lg w-full text-left resize-none content-text-size p-4 ${
                      errors.message ? "border-red-500" : ""
                    }`}
                    style={{ height: "clamp(8rem, 20vh, 16rem)" }}
                  ></textarea>
                  {errors.message && (
                    <p className="text-red-600 text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                <ButtonElement
                  btnText="Send Message"
                  onClick={() => {}}
                  className="h1 font-bold hover-text-responsive transition-all ease-in-out duration-500  h-16 rounded-lg"
                  size="default"
                  type="submit"
                />
              </motion.form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
